import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { usePageContent, usePageSections, usePageFeatures } from "@/lib/siteContent";

/**
 * Reusable luxury page shell.
 *
 * Any page passing a `slug` prop is auto-hydrated from Shopify's `page_content`
 * metaobject (see SHOPIFY_PAGES_SETUP.md). Fields in Shopify override the
 * hardcoded props; empty Shopify fields keep the hardcoded fallback.
 *
 * If Shopify has a `body` value for the page, it renders BEFORE any React
 * children as free-form paragraphs (blank line = new paragraph). This lets the
 * user append long copy without editing code.
 */
export function PageShell({ slug, eyebrow, title, intro, children, breadcrumb, testid }) {
  const override = usePageContent(slug || testid || "");

  const effectiveEyebrow = override.eyebrow || eyebrow;
  const effectiveTitle = override.title || title;
  const effectiveIntro = override.intro || intro;
  const shopifyBody = override.body || "";

  const idBase = testid || (effectiveTitle || "page").toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <main data-testid={`page-${idBase}`} className="bg-[#FAF8F5]">
      {/* Hero band */}
      <section className="border-b border-black/10 bg-[#F1ECE4]/40 sd-noise">
        <div className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <nav aria-label="breadcrumb" className="text-xs text-[#1A1918]/50 flex items-center gap-1.5 mb-5">
            <Link to="/" className="hover:text-[#C5A059] transition-colors">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#1A1918]/80">{breadcrumb || effectiveTitle}</span>
          </nav>
          {effectiveEyebrow ? (
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059] mb-3">{effectiveEyebrow}</div>
          ) : null}
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A1918] leading-[1.05] max-w-3xl"
          >
            {effectiveTitle}
          </motion.h1>
          {effectiveIntro ? (
            <p className="mt-5 text-[#1A1918]/70 leading-relaxed max-w-2xl text-[15px]">{effectiveIntro}</p>
          ) : null}
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
        {/* Shopify-editable body — renders BEFORE any React-defined children so
            store owners can prepend/replace copy without touching code. */}
        {shopifyBody ? (
          <div className="max-w-3xl mb-14 space-y-4 text-[15px] text-[#1A1918]/80 leading-[1.75]" data-testid={`page-body-${idBase}`}>
            {shopifyBody
              .split(/\n{2,}/)
              .filter(Boolean)
              .map((para, i) => (
                <p key={i}>{para}</p>
              ))}
          </div>
        ) : null}
        {children}
      </section>
    </main>
  );
}

/** Section block helper — used inside PageShell body */
export function Section({ title, children, className = "" }) {
  return (
    <section className={`mb-14 last:mb-0 ${className}`}>
      {title ? (
        <h2 className="font-serif text-xl sm:text-2xl text-[#1A1918] mb-4">{title}</h2>
      ) : null}
      <div className="space-y-4 text-[15px] text-[#1A1918]/80 leading-[1.75] max-w-3xl">{children}</div>
    </section>
  );
}

/** Two-column feature grid used across marketing pages */
export function FeatureGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {items.map((it, i) => (
        <div
          key={i}
          className="p-6 rounded-2xl border border-black/10 bg-[#FAF8F5] hover:border-[#C5A059]/40 transition-colors"
        >
          {it.badge ? (
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-2">{it.badge}</div>
          ) : null}
          <div className="font-serif text-lg text-[#1A1918] mb-2">{it.title}</div>
          <p className="text-[13.5px] text-[#1A1918]/70 leading-[1.7]">{it.body}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Editable Section — reads from Shopify `page_section` metaobjects
 * scoped to (slug + sectionKey). Falls back to `fallback` prop when Shopify
 * has no matching entries.
 *
 * `fallback` shape: [{ heading: string, body: ReactNode|string }]
 * If body is a string it renders as paragraphs; if it's a React node it renders as-is.
 *
 * When Shopify has ≥1 matching page_section, the ENTIRE fallback list is
 * replaced by the Shopify list (all-or-nothing, keeps mental model simple).
 */
export function EditableSection({ slug, sectionKey = "default", fallback = [], as = "section" }) {
  const overrides = usePageSections(slug, sectionKey);
  const items = overrides.length > 0
    ? overrides.map((o) => ({ heading: o.heading, body: o.body }))
    : fallback;

  if (items.length === 0) return null;

  return items.map((item, i) => (
    <Section key={`${sectionKey}-${i}`} title={item.heading}>
      {typeof item.body === "string"
        ? item.body
            .split(/\n{2,}/)
            .filter(Boolean)
            .map((para, j) => <p key={j}>{para}</p>)
        : item.body}
    </Section>
  ));
}

/**
 * Editable Feature Grid — reads from Shopify `page_feature` metaobjects
 * scoped to (slug + sectionKey). Falls back to `fallback` prop when empty.
 *
 * `fallback` shape: [{ badge, title, body }]
 */
export function EditableFeatureGrid({ slug, sectionKey = "default", fallback = [] }) {
  const overrides = usePageFeatures(slug, sectionKey);
  const items = overrides.length > 0
    ? overrides.map((o) => ({ badge: o.badge, title: o.title, body: o.body }))
    : fallback;
  if (items.length === 0) return null;
  return <FeatureGrid items={items} />;
}

export default PageShell;
